<template>
    <div class="flex flex-row w-full">
        <div class="flex flex-col gap-1 pr-1 justify-center shrink-0">
            <slot />
        </div>

        <div class="h-[calc(100%-0.5rem)] w-px bg-gray-300 my-1" />

        <div class="flex flex-1 flex-col p-1 min-w-29 justify-center items-center text-center px-1 gap-2">
            <div class="flex flex-row gap-0.5 items-center">
                <div class="text-lg leading-none ml-1 min-w-max">
                    <span class="font-bold tabular-nums">{{ formattedSumOccupied }}</span>
                    <span class="font-light text-gray-500">&thinsp;m²</span>
                </div>

                <span class="text-gray-300 px-0.5 font-light">&ndash;</span>

                <div class="flex flex-row items-center font-bold tabular-nums">
                    {{ props.countOccupied }}&thinsp;<Tooltip>
                        <template #trigger="tooltipProps">
                            <ExhibitionAreaIcon
                                class="size-4.5 text-gray-500 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-700 rounded-xs"
                                tabindex="0"
                                v-bind="tooltipProps"
                            />
                        </template>
                        <template #text>Ausstellungsfläche</template>
                    </Tooltip>
                </div>
            </div>

            <template v-if="props.sumOptional !== undefined && props.sumOptional > 0">
                <div class="flex flex-col items-center w-full">
                    <div class="flex flex-row items-center w-full px-1">
                        <div aria-hidden="true" class="w-full h-px bg-gray-300" />
                        <div class="relative flex justify-center">
                            <div class="text-xxs leading-[1.2] font-light min-w-max px-1.5 text-gray-500">
                                {{ $t('optionally-occupied-up-to') }}
                            </div>
                        </div>
                        <div aria-hidden="true" class="w-full h-px bg-gray-300" />
                    </div>

                    <div class="flex flex-row gap-0.5 items-center text-xs/[1.2]">
                        <div class="font-semibold tabular-nums">
                            {{ formattedSumTotal }}<span class="font-light text-gray-500">&thinsp;m²</span>
                        </div>
                        <template v-if="props.countTotal">
                            <span class="font-light text-gray-300 px-0.5">&ndash;</span>
                            <div class="flex flex-row items-center font-semibold tabular-nums">
                                {{ props.countTotal }}&thinsp;<Tooltip>
                                    <template #trigger="tooltipProps">
                                        <ExhibitionAreaIcon
                                            class="size-3.5 text-gray-500 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-700 rounded-xs"
                                            tabindex="0"
                                            v-bind="tooltipProps"
                                        />
                                    </template>
                                    <template #text>Ausstellungsfläche</template>
                                </Tooltip>
                            </div>
                        </template>
                    </div>

                    <div
                        class="flex flex-row items-center justify-center gap-0.5 text-xxs leading-[1.2] w-full font-light tabular-nums text-gray-500"
                    >
                        <div class="font-light">
                            (+&thinsp;<span class="font-normal text-gray-600">{{ formattedSumOptional }}</span
                            >&thinsp;m²
                        </div>
                        <template v-if="props.countOptional">
                            <span class="text-gray-300 px-0.5 font-light">&ndash;</span>
                            <div class="flex flex-row items-center">
                                <span class="font-normal text-gray-600 tabular-nums">{{ props.countOptional }}</span
                                >&thinsp;<Tooltip>
                                    <template #trigger="tooltipProps">
                                        <ExhibitionAreaIcon
                                            class="size-3 text-gray-500 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-700 rounded-xs"
                                            tabindex="0"
                                            v-bind="tooltipProps"
                                        />
                                    </template>
                                    <template #text>Ausstellungsfläche</template>
                                </Tooltip>
                                <span class="text-gray-400">)</span>
                            </div>
                        </template>
                        <template v-else>
                            <span class="text-gray-400">)</span>
                        </template>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import ExhibitionAreaIcon from '../Icon/ExhibitionAreaIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';

type LocationDotsTileProps = {
    sumOccupied: number;
    sumOptional?: number;
    sumTotal?: number;
    countOccupied: number;
    countOptional?: number;
    countTotal?: number;
};

const props = defineProps<LocationDotsTileProps>();

const formattedSumOccupied = new Intl.NumberFormat('de-DE', {
    style: 'decimal',
    maximumFractionDigits: 0,
}).format(props.sumOccupied);

const formattedSumOptional = new Intl.NumberFormat('de-DE', {
    style: 'decimal',
    maximumFractionDigits: 0,
}).format(props.sumOptional ?? 0);

const formattedSumTotal = new Intl.NumberFormat('de-DE', {
    style: 'decimal',
    maximumFractionDigits: 0,
}).format(props.sumTotal ?? 0);
</script>
